import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Loading } from '@appkit4/react-components/loading';
import { Modal } from '@appkit4/react-components/modal';
import { Switch } from '@appkit4/react-components/switch';
import useLocale from '../../../locale/useLocale';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import './DocTypeSettings.scss';
import { AppRoutes } from '../../../Constants';
import useToast from '../../../helpers/useToast';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import type { AxiosError } from 'axios';
import {
    useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields,
    usePostV1AdminUpdateDocumentTypeFields,
    usePostV1AdminToggleValidationEnabled,
    useGetV1DocumentTypeDocumentTypeId,
    usePatchV1DocumentTypeDocumentTypeId,
} from '../../../api/generated/endpoint';
import type {
    DocumentTypeFieldResponse,
    UpdateDocumentTypeFieldsRequest,
    ToggleValidationEnabledRequest,
    DocumentType,
} from '../../../api/generated/models';
import DraggableFieldsTable from './SortableTableRow';

const ExtractionDocTypeSettings = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { l } = useLocale();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const docType = searchParams.get('docType');
    const docTypeId = parseInt(searchParams.get('docTypeId') || '0', 10);

    const [isChanged, setIsChanged] = useState(false);
    const [data, setData] = useState<DocumentTypeFieldResponse[]>([]);
    const [isValidationEnabled, setIsValidationEnabled] = useState(false);
    const [minConfidence, setMinConfidence] = useState('');
    const [maxConfidence, setMaxConfidence] = useState('');
    const [fuzzyKeywords, setFuzzyKeywords] = useState('');
    const [antiFuzzyKeywords, setAntiFuzzyKeywords] = useState('');

    const processTypeId = parseInt(
        searchParams.get('processTypeId') || '0',
        10
    );

    const {
        data: fieldsData,
        isLoading,
        error,
    } = useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields(docTypeId, {
        processTypeId,
    });

    const { data: documentTypeData, isLoading: isDocumentTypeLoading } =
        useGetV1DocumentTypeDocumentTypeId(docTypeId);

    useEffect(() => {
        if (isChanged == false) {
            if (fieldsData) {
                setData(fieldsData.documentTypeFields ?? []);
                setIsValidationEnabled(fieldsData.validationEnabled ?? false);
            }
        }
    }, [fieldsData]);

    useEffect(() => {
        if (documentTypeData) {
            setMinConfidence(
                documentTypeData.minimumClassifierConfidence?.toString() || ''
            );
            setMaxConfidence(
                documentTypeData.maximumClassifierConfidence?.toString() || ''
            );
            setFuzzyKeywords(documentTypeData.classifierFuzzyKeywords || '');
            setAntiFuzzyKeywords(
                documentTypeData.classifierFuzzyAntiKeywords || ''
            );
        }
    }, [documentTypeData]);

    const mutation = usePostV1AdminUpdateDocumentTypeFields();
    const toggleValidationMutation = usePostV1AdminToggleValidationEnabled();
    const updateDocumentTypeMutation = usePatchV1DocumentTypeDocumentTypeId();

    const [isConfidenceModalOpen, setIsConfidenceModalOpen] = useState(false);
    const [isFuzzyModalOpen, setIsFuzzyModalOpen] = useState(false);
    const [confidenceValue, setConfidenceValue] = useState('');
    const [isApplyingConfidence, setIsApplyingConfidence] = useState(false);

    const handleSave = async (updatedFields: any) => {
        try {
            const updateRequest: UpdateDocumentTypeFieldsRequest = {
                documentTypeId: docTypeId,
                fields: updatedFields.map((field: any) => ({
                    documentTypeFieldId: field.fieldId,
                    fieldName: field.fieldName,
                    minimumConfidence: field.minimumConfidence,
                    minimumConfidenceActive:
                        field.minimumConfidenceActive ?? false,
                    normalisationPrompt: field.normalisationPrompt,
                    orderNumber: field.orderNumber ?? 0,
                })),
            };

            await mutation.mutateAsync({ data: updateRequest });
            setIsChanged(false);
            showToast(l('_success'), l('_docTypeSettingsSaved'), 'success');
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        }
    };

    const handleOverwriteConfidence = () => {
        setIsConfidenceModalOpen(true);
    };

    const handleFuzzyClassifier = () => {
        setIsFuzzyModalOpen(true);
    };

    const handleApplyConfidence = async () => {
        try {
            setIsApplyingConfidence(true);
            const confidenceDecimal =
                Math.round(parseFloat(confidenceValue) * 10000) / 1000000;

            const newData = data.map((field) => ({
                ...field,
                minimumConfidence: confidenceDecimal,
                minimumConfidenceActive: true,
            }));

            setData(newData);
            const updateRequest: UpdateDocumentTypeFieldsRequest = {
                documentTypeId: docTypeId,
                fields: newData.map((field) => ({
                    documentTypeFieldId: field.fieldId,
                    fieldName: field.fieldName,
                    minimumConfidence: field.minimumConfidence,
                    minimumConfidenceActive:
                        field.minimumConfidenceActive ?? false,
                })),
            };

            await mutation.mutateAsync({ data: updateRequest });
            showToast(l('_success'), l('_docTypeSettingsSaved'), 'success');
            setIsConfidenceModalOpen(false);
            setConfidenceValue('');
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        } finally {
            setIsApplyingConfidence(false);
        }
    };

    const handleApplyFuzzyClassifier = async () => {
        try {
            setIsApplyingConfidence(true);

            const minConfidenceValue = minConfidence
                ? parseFloat(minConfidence)
                : undefined;
            const maxConfidenceValue = maxConfidence
                ? parseFloat(maxConfidence)
                : undefined;

            if (
                minConfidenceValue !== undefined &&
                maxConfidenceValue !== undefined &&
                minConfidenceValue > maxConfidenceValue
            ) {
                showToast(
                    'Validation Error',
                    'Minimum confidence cannot be greater than maximum confidence',
                    'error'
                );
                return;
            }

            const updateRequest: DocumentType = {
                ...documentTypeData,
                minimumClassifierConfidence: minConfidenceValue,
                maximumClassifierConfidence: maxConfidenceValue,
                classifierFuzzyKeywords: fuzzyKeywords || null,
                classifierFuzzyAntiKeywords: antiFuzzyKeywords || null,
            };

            await updateDocumentTypeMutation.mutateAsync({
                documentTypeId: docTypeId,
                data: updateRequest,
            });

            showToast(
                l('_success'),
                'Fuzzy classifier settings saved successfully',
                'success'
            );
            setIsFuzzyModalOpen(false);
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        } finally {
            setIsApplyingConfidence(false);
        }
    };

    const handleToggleValidation = async (checked: boolean) => {
        try {
            const toggleRequest: ToggleValidationEnabledRequest = {
                documentTypeId: docTypeId,
                enableValidation: checked,
            };
            await toggleValidationMutation.mutateAsync({ data: toggleRequest });
            setIsValidationEnabled(checked);
            showToast(l('_success'), l('_validationToggled'), 'success');
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        }
    };

    if (isLoading || isDocumentTypeLoading)
        return <Loading loadingType="circular" indeterminate={true} />;
    if (error) return <div>An error occurred: {error.message}</div>;

    return (
        <div className="doctype-settings">
            <div className="doctype-breadcrumb-container">
                <span
                    className="Appkit4-icon icon-arrow-left-outline"
                    role="button"
                    onClick={() => navigate(AppRoutes.ExtractionSettings)}
                ></span>
                <Breadcrumbs items={[{ label: docType || '', href: '' }]} />
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                }}
            >
                <h2 className="ap-typography-heading-m title">{docType}</h2>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Button
                            kind="primary"
                            style={{
                                marginRight: '10px',
                            }}
                            onClick={handleFuzzyClassifier}
                        >
                            Add Fuzzy Classifier
                        </Button>
                        <span>Enable Validation</span>
                        <Switch
                            checked={isValidationEnabled}
                            onChange={handleToggleValidation}
                        />
                    </div>
                    <Button kind="tertiary" onClick={handleOverwriteConfidence}>
                        Overwrite Confidence Level
                    </Button>
                </div>
            </div>
            <div className="table-container">
                <div className="table-title">
                    <span>Document type settings</span>
                </div>
                {/* <Table
                    className="extraction-table"
                    originalData={data}
                    hasTitle
                    condensed
                    style={{ borderRadius: '0px' }}
                >
                    <Column
                        field="fieldName"
                        renderCell={(row) => (
                            <Input
                                style={{ maxWidth: '400px' }}
                                type="text"
                                value={row.fieldName || ''}
                                onChange={(value: string) => {
                                    setIsChanged(true);
                                    const newData = [...data];
                                    const index = newData.findIndex(
                                        (d) => d.fieldId === row.fieldId
                                    );
                                    newData[index].fieldName = value;
                                    setData(newData);
                                }}
                                hideTitleOnInput
                            />
                        )}
                    >
                        Field name
                    </Column>
                    <Column
                        field="fieldTypeId"
                        renderCell={(row) => fieldTypeToString(row.fieldTypeId)}
                    >
                        Field type
                    </Column>
                    <Column
                        field="minimumConfidence"
                        renderCell={(row) => (
                            <div className="minimum-confidence-container">
                                <Input
                                    style={{ maxWidth: '300px' }}
                                    type="number"
                                    value={
                                        row.minimumConfidence !== undefined &&
                                        row.minimumConfidence !== null
                                            ? Math.round(
                                                  row.minimumConfidence * 100
                                              ).toString()
                                            : ''
                                    }
                                    onChange={(value: string) => {
                                        if (value === '') {
                                            const newData = [...data];
                                            const index = newData.findIndex(
                                                (d) => d.fieldId === row.fieldId
                                            );
                                            newData[index].minimumConfidence =
                                                null;
                                            setData(newData);
                                            setIsChanged(true);
                                            return;
                                        }

                                        const numValue = parseFloat(value);
                                        if (isNaN(numValue)) return;

                                        const clampedValue = Math.min(
                                            Math.max(numValue, 0),
                                            100
                                        );

                                        const newData = [...data];
                                        const index = newData.findIndex(
                                            (d) => d.fieldId === row.fieldId
                                        );
                                        newData[index].minimumConfidence =
                                            Math.round(clampedValue * 10000) /
                                            1000000;
                                        setData(newData);
                                        setIsChanged(true);
                                    }}
                                    min={0}
                                    max={100}
                                    hideTitleOnInput
                                />
                                <span className="confidence-percentage">%</span>
                            </div>
                        )}
                    >
                        Minimum confidence
                    </Column>
                    <Column
                        field="fieldName"
                        style={{ width: '200px' }}
                        renderCell={(row) => (
                            <span
                                style={{
                                    color: isValidationEnabled
                                        ? '#474747'
                                        : '#c0c0c0',
                                    fontSize: '20px',
                                    cursor: isValidationEnabled
                                        ? 'pointer'
                                        : 'not-allowed',
                                    marginLeft: '60px',
                                }}
                                className="Appkit4-icon icon-setting-outline"
                                role="button"
                                aria-label="Settings"
                                onClick={() => {
                                    if (isValidationEnabled) {
                                        navigate(
                                            `${
                                                AppRoutes.ExtractionValidationSettings
                                            }?docType=${docType}&docTypeId=${docTypeId}&fieldName=${
                                                row.fieldName
                                            }&fieldType=${fieldTypeToString(
                                                row.fieldTypeId
                                            )}&fieldId=${
                                                row.fieldId
                                            }&processTypeId=${processTypeId}`
                                        );
                                    }
                                }}
                            />
                        )}
                    >
                        Validation settings
                    </Column>
                </Table> */}
                {data?.length && (
                    <DraggableFieldsTable
                        fields={data}
                        onFieldsUpdate={(updatedFields: any) => {
                            handleSave(updatedFields);
                        }}
                        isValidationEnabled={isValidationEnabled}
                    />
                )}
                {/* <div className="table-footer">
                    <Button
                        kind="secondary"
                        disabled={!isChanged}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </div> */}
                <Modal
                    visible={isConfidenceModalOpen}
                    onCancel={() => {
                        setIsConfidenceModalOpen(false);
                        setConfidenceValue('');
                    }}
                    className="overwrite-confidence"
                    title="Overwrite Confidence Level"
                    footer={
                        <div>
                            <Button
                                kind="secondary"
                                onClick={handleApplyConfidence}
                                loading={isApplyingConfidence}
                                disabled={
                                    !confidenceValue || isApplyingConfidence
                                }
                            >
                                Apply
                            </Button>
                        </div>
                    }
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            Enter the confidence level percentage to apply to
                            all fields:
                        </div>
                        <div className="minimum-confidence-modal">
                            <Input
                                type="number"
                                value={confidenceValue}
                                onChange={(value: string) => {
                                    if (value === '') {
                                        setConfidenceValue('');
                                        return;
                                    }
                                    const numValue = parseFloat(value);
                                    if (isNaN(numValue)) {
                                        return;
                                    }
                                    const clampedValue = Math.min(
                                        Math.max(numValue, 0),
                                        100
                                    );
                                    setConfidenceValue(clampedValue.toString());
                                }}
                                placeholder="Enter confidence level"
                                style={{ maxWidth: '300px' }}
                                min={0}
                                max={100}
                            />
                            <span className="confidence-percentage">%</span>
                        </div>
                    </div>
                </Modal>
                <Modal
                    visible={isFuzzyModalOpen}
                    onCancel={() => {
                        setIsFuzzyModalOpen(false);
                        setMinConfidence(
                            documentTypeData?.minimumClassifierConfidence?.toString() ||
                                ''
                        );
                        setMaxConfidence(
                            documentTypeData?.maximumClassifierConfidence?.toString() ||
                                ''
                        );
                        setFuzzyKeywords(
                            documentTypeData?.classifierFuzzyKeywords || ''
                        );
                        setAntiFuzzyKeywords(
                            documentTypeData?.classifierFuzzyAntiKeywords || ''
                        );
                    }}
                    className="fuzzy-classifier-modal"
                    title="Fuzzy Classifier Settings"
                    footer={
                        <Button
                            kind="secondary"
                            onClick={handleApplyFuzzyClassifier}
                            loading={isApplyingConfidence}
                            disabled={
                                isApplyingConfidence ||
                                (minConfidence &&
                                    maxConfidence &&
                                    parseFloat(minConfidence) >
                                        parseFloat(maxConfidence))
                            }
                        >
                            Apply
                        </Button>
                    }
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            Configure fuzzy classifier settings for this
                            document type:
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                }}
                            >
                                Minimum Classification Confidence
                            </label>
                            <div className="minimum-confidence-modal">
                                <Input
                                    type="number"
                                    value={minConfidence}
                                    onChange={(value: string) => {
                                        if (value === '') {
                                            setMinConfidence('');
                                            return;
                                        }
                                        const numValue = parseFloat(value);
                                        if (isNaN(numValue)) {
                                            return;
                                        }
                                        const clampedValue = Math.min(
                                            Math.max(numValue, 0),
                                            1
                                        );
                                        setMinConfidence(
                                            clampedValue.toString()
                                        );
                                    }}
                                    placeholder="Enter minimum confidence (0-1)"
                                    style={{ maxWidth: '300px' }}
                                    min={0}
                                    max={1}
                                    step="0.01"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                }}
                            >
                                Maximum Classification Confidence
                            </label>
                            <div className="minimum-confidence-modal">
                                <Input
                                    type="number"
                                    value={maxConfidence}
                                    onChange={(value: string) => {
                                        if (value === '') {
                                            setMaxConfidence('');
                                            return;
                                        }
                                        const numValue = parseFloat(value);
                                        if (isNaN(numValue)) {
                                            return;
                                        }
                                        const clampedValue = Math.min(
                                            Math.max(numValue, 0),
                                            1
                                        );
                                        setMaxConfidence(
                                            clampedValue.toString()
                                        );
                                    }}
                                    placeholder="Enter maximum confidence (0-1)"
                                    style={{
                                        maxWidth: '300px',
                                        borderColor:
                                            minConfidence &&
                                            maxConfidence &&
                                            parseFloat(minConfidence) >
                                                parseFloat(maxConfidence)
                                                ? '#ff4444'
                                                : undefined,
                                    }}
                                    min={0}
                                    max={1}
                                    step="0.01"
                                />
                            </div>
                            {minConfidence &&
                                maxConfidence &&
                                parseFloat(minConfidence) >
                                    parseFloat(maxConfidence) && (
                                    <div
                                        style={{
                                            color: '#ff4444',
                                            fontSize: '12px',
                                            marginTop: '4px',
                                        }}
                                    >
                                        Maximum confidence must be greater than
                                        or equal to minimum confidence
                                    </div>
                                )}
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                }}
                            >
                                Fuzzy Keywords
                            </label>
                            <Input
                                type="text"
                                value={fuzzyKeywords}
                                onChange={(value: string) =>
                                    setFuzzyKeywords(value)
                                }
                                placeholder="Enter fuzzy keywords"
                                style={{ maxWidth: '400px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontWeight: '500',
                                }}
                            >
                                Anti-Fuzzy Keywords
                            </label>
                            <Input
                                type="text"
                                value={antiFuzzyKeywords}
                                onChange={(value: string) =>
                                    setAntiFuzzyKeywords(value)
                                }
                                placeholder="Enter anti-fuzzy keywords"
                                style={{ maxWidth: '400px' }}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default ExtractionDocTypeSettings;
